import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

const RAG = {
  Green: "bg-emerald-500/15 text-emerald-700 border-emerald-500/30",
  Amber: "bg-amber-500/15 text-amber-700 border-amber-500/30",
  Red: "bg-red-500/15 text-red-700 border-red-500/30",
};
const DOT = { Green: "bg-emerald-500", Amber: "bg-amber-500", Red: "bg-red-500" };

export const RagBadge = ({ value, testid }) => (
  <span data-testid={testid} className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-xs font-semibold ${RAG[value] || "bg-slate-100 text-slate-600 border-slate-200"}`}>
    <span className={`h-1.5 w-1.5 rounded-full ${DOT[value] || "bg-slate-400"}`} />
    {value || "—"}
  </span>
);

export const RagCell = ({ value }) => (
  <div title={value} className={`h-7 w-full min-w-[44px] rounded ${DOT[value] || "bg-slate-200"} opacity-90`} />
);

export const Pill = ({ children, tone = "slate", testid }) => {
  const t = { slate: "bg-slate-100 text-slate-700", indigo: "bg-indigo-50 text-indigo-700", green: "bg-emerald-50 text-emerald-700",
    red: "bg-red-50 text-red-700", amber: "bg-amber-50 text-amber-700" }[tone];
  return <span data-testid={testid} className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ${t}`}>{children}</span>;
};

export const statusTone = (s) => ({ approved: "green", accepted: "green", applied: "green", done: "green", posted: "green", matched: "green", complete: "green", published: "green",
  rejected: "red", exception: "red", escalated: "amber", pending: "amber", in_approval: "amber", tech_review: "amber", proposed: "amber", open: "indigo" }[s] || "slate");

export const PageHeader = ({ overline, title, subtitle, actions }) => (
  <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between fade-up">
    <div>
      {overline && <div className="overline mb-1">{overline}</div>}
      <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">{title}</h1>
      {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
    </div>
    {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
  </div>
);

export const Panel = ({ title, action, children, className = "", testid }) => (
  <section data-testid={testid} className={`rounded-xl border border-slate-200 bg-white shadow-sm fade-up ${className}`}>
    {title && (
      <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
        <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
        {action}
      </div>
    )}
    <div className="p-5">{children}</div>
  </section>
);

export const Stat = ({ label, value, hint, testid }) => (
  <div data-testid={testid} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm fade-up">
    <div className="text-xs font-medium uppercase tracking-wider text-slate-500">{label}</div>
    <div className="mt-2 font-display text-2xl font-semibold text-slate-900">{value}</div>
    {hint && <div className="mt-1 text-xs text-slate-500">{hint}</div>}
  </div>
);

export const Empty = ({ text = "Nothing here yet" }) => <div className="py-6 text-center text-sm text-slate-400">{text}</div>;

export const Table = ({ cols, rows, testid, empty }) => (
  <div className="overflow-x-auto" data-testid={testid}>
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
          {cols.map((c) => <th key={c.k} className="whitespace-nowrap px-3 py-2 font-medium">{c.h}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 && <tr><td colSpan={cols.length}><Empty text={empty} /></td></tr>}
        {rows.map((r, i) => (
          <tr key={r.id || i} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
            {cols.map((c) => <td key={c.k} className="px-3 py-2.5 align-top text-slate-700">{c.r ? c.r(r) : r[c.k]}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const PHASES = ["Intake", "Concept", "Planning", "Execution", "Closure", "Closed"];
export const PhaseStepper = ({ phase }) => {
  const idx = PHASES.indexOf(phase);
  return (
    <div className="flex items-center gap-1 overflow-x-auto" data-testid="phase-stepper">
      {PHASES.map((p, i) => (
        <div key={p} className="flex items-center gap-1">
          <div className={`flex items-center gap-1.5 whitespace-nowrap rounded-md px-2.5 py-1 text-xs font-medium ${i < idx ? "bg-indigo-50 text-indigo-700" : i === idx ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-400"}`}>
            {i < idx && <Check className="h-3 w-3" />}{p}
          </div>
          {i < PHASES.length - 1 && <div className="text-[10px] font-mono text-slate-400">G{i}</div>}
        </div>
      ))}
    </div>
  );
};

export const CompanyFilter = ({ value, onChange, testid = "company-filter" }) => {
  const { data } = useLoad(() => api.get("/companies").then((r) => r.data));
  return (
    <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="h-9 rounded-md border border-input bg-white px-3 text-sm">
      <option value="">All companies</option>
      {(data || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
      <option value="unassigned">Unassigned</option>
    </select>
  );
};

function Field({ f, value, onChange }) {
  const id = `f-${f.name}`;
  const tid = `field-${f.name}`;
  if (f.type === "checkbox")
    return (
      <label className="flex items-start gap-2 text-sm">
        <input data-testid={tid} type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="mt-1 h-4 w-4 accent-indigo-600" />
        <span><span className="font-medium">{f.label}</span>{f.help && <span className="block text-xs text-slate-500">{f.help}</span>}</span>
      </label>
    );
  if (f.type === "multicheck") {
    const sel = value || [];
    const toggle = (v) => onChange(sel.includes(v) ? sel.filter((x) => x !== v) : [...sel, v]);
    return (
      <div className="space-y-1.5" data-testid={tid}>
        <Label>{f.label}{f.required && <span className="text-red-500"> *</span>}</Label>
        <div className="flex flex-wrap gap-2">
          {f.options.map((o) => (
            <label key={o.value} className={`flex cursor-pointer items-center gap-1.5 rounded-full border px-3 py-1 text-sm transition-colors ${sel.includes(o.value) ? "border-indigo-500 bg-indigo-50 text-indigo-800" : "border-slate-200 bg-white"}`}>
              <input type="checkbox" data-testid={`${tid}-${o.value}`} checked={sel.includes(o.value)} onChange={() => toggle(o.value)} className="h-3.5 w-3.5 accent-indigo-600" />{o.label}
            </label>
          ))}
        </div>
        {f.help && <p className="text-xs text-slate-500">{f.help}</p>}
      </div>
    );
  }
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{f.label}{f.required && <span className="text-red-500"> *</span>}</Label>
      {f.type === "textarea" ? (
        <Textarea id={id} data-testid={tid} value={value ?? ""} onChange={(e) => onChange(e.target.value)} rows={3} />
      ) : f.type === "select" ? (
        <select id={id} data-testid={tid} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="h-9 w-full rounded-md border border-input bg-white px-3 text-sm">
          <option value="">Select…</option>
          {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      ) : (
        <Input id={id} data-testid={tid} type={f.type || "text"} value={value ?? ""} onChange={(e) => onChange(f.type === "number" ? e.target.value : e.target.value)} />
      )}
      {f.help && <p className="text-xs text-slate-500">{f.help}</p>}
    </div>
  );
}

export function FormDialog({ open, onOpenChange, title, description, fields, initial = {}, onSubmit, submitLabel = "Save", testid = "form-dialog", children }) {
  const [vals, setVals] = useState(initial);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setVals(initial);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const submit = async (e) => {
    e.preventDefault();
    const missing = fields.filter((f) => f.required && (vals[f.name] === undefined || vals[f.name] === "" || (Array.isArray(vals[f.name]) && !vals[f.name].length)));
    if (missing.length) return toast.error(`Required: ${missing.map((m) => m.label).join(", ")}`);
    const out = { ...vals };
    fields.filter((f) => f.type === "number").forEach((f) => { if (out[f.name] !== undefined && out[f.name] !== "") out[f.name] = Number(out[f.name]); });
    setBusy(true);
    try {
      await onSubmit(out);
      onOpenChange(false);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={testid} className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display">{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {fields.map((f) => <Field key={f.name} f={f} value={vals[f.name]} onChange={(v) => setVals((s) => ({ ...s, [f.name]: v }))} />)}
          {children}
          <DialogFooter>
            <Button type="submit" disabled={busy} data-testid={`${testid}-submit`}>{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{submitLabel}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function useLoad(fn, deps = []) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const reload = async () => {
    try {
      setData(await fn());
      setError(null);
    } catch (e) {
      setError(errMsg(e));
    }
  };
  useEffect(() => {
    reload();
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
  return { data, error, reload };
}

export const Loading = ({ error }) => (
  <div className="py-16 text-center text-sm text-slate-500" data-testid={error ? "load-error" : "loading"}>
    {error ? <span className="text-red-600">{error}</span> : <Loader2 className="mx-auto h-5 w-5 animate-spin" />}
  </div>
);

export async function act(promise, ok) {
  try {
    const r = await promise;
    if (ok) toast.success(ok);
    return r;
  } catch (e) {
    toast.error(errMsg(e));
    return null;
  }
}
